import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ALERT_TYPE, BaseError, REDIRECT_TYPE } from '../errors';

const IGNORE_TYPE = 'ignore';
const HTTP_SERVISE_FEATURE = 'HttpService';

export type THttpResponse<T, V> = AxiosResponse<T, V>;

export type Unpromisify<T> = T extends Promise<infer U> ? U : T;

type TApiFunctionReturn<T extends (...args: any) => Promise<unknown>> = ReturnType<T>;

export type TApiFetchResponse<T extends (...args: any) => Promise<unknown>> = Unpromisify<TApiFunctionReturn<T>>;

export function isAxiosError<T>(error: any): error is AxiosError<T> {
  return !!error.isAxiosError;
}

export function isNetworkError<T>(error: AxiosError<T>): boolean {
  return !!error.message.includes('Network Error');
}

export function isRequestAbortedError<T>(error: AxiosError<T>): boolean {
  return !!error.message.includes('Request aborted');
}

export class HttpError extends BaseError {
  data: any;
  status: number;
  baseError: AxiosError | Error;
  constructor(error: AxiosError | Error) {
    super(error.message);
    this.constructor = HttpError;
    Object.setPrototypeOf(this, HttpError.prototype);
    this.feature = HTTP_SERVISE_FEATURE;
    this.data = isAxiosError(error) ? error.response?.data : undefined;
    this.status = isAxiosError(error) && error.response ? error.response.status : 0;
    this.baseError = error;
  }
}

export class RequestAbortedError extends HttpError {
  constructor(error: AxiosError) {
    super(error);
    this.constructor = RequestAbortedError;
    Object.setPrototypeOf(this, RequestAbortedError.prototype);
    this.errorType = IGNORE_TYPE;
  }
}

export class NetworkError extends HttpError {
  constructor(error: AxiosError) {
    super(error);
    this.constructor = NetworkError;
    Object.setPrototypeOf(this, NetworkError.prototype);
    this.errorType = ALERT_TYPE;
  }
}

export class NotAuthorizedError extends HttpError {
  constructor(error: AxiosError) {
    super(error);
    this.constructor = NotAuthorizedError;
    Object.setPrototypeOf(this, NotAuthorizedError.prototype);
    this.errorType = REDIRECT_TYPE;
  }
}

type IHttpServiceGetRequest = <T>(url: string, config?: AxiosRequestConfig | undefined) => Promise<AxiosResponse<T>>;
type IHttpServiceRequest = <T>(config: AxiosRequestConfig) => Promise<AxiosResponse<T>>;

export interface IHttpService {
  setBaseURL: (url: string) => void;
  setToken: (token: string) => void;
  get: IHttpServiceGetRequest;
  request: IHttpServiceRequest;
}

function createAxiosInstance(baseURL?: string, timeout?: number) {
  return axios.create({
    baseURL,
    timeout,
  });
}

function setBaseURLBuilder(instance: AxiosInstance) {
  return (url: string) => {
    instance.defaults.baseURL = url;
  };
}

function getAxiosInstance(baseURL?: string, instanceOverride?: AxiosInstance, timeout?: number) {
  let instance = !instanceOverride ? createAxiosInstance(baseURL, timeout) : instanceOverride;
  if (instanceOverride && baseURL) {
    setBaseURLBuilder(instance)(baseURL);
  }
  return instance;
}

export function wrapError(error: AxiosError | Error) {
  let serviceError;
  if (isAxiosError(error) && error.response && error.response.status === 401) {
    serviceError = new NotAuthorizedError(error);
  } else if (isAxiosError(error) && isNetworkError(error)) {
    serviceError = new NetworkError(error);
  } else if (isAxiosError(error) && isRequestAbortedError(error)) {
    serviceError = new RequestAbortedError(error);
  } else {
    serviceError = new HttpError(error);
  }
  return Promise.reject(serviceError);
}

export function getHttpService(baseURL?: string, instanceOverride?: AxiosInstance, timeout?: number) {
  const axiosInstance = getAxiosInstance(baseURL, instanceOverride, timeout);
  let token: string;
  const setToken = (value: string) => {
    token = value;
  };
  const setBaseURL = setBaseURLBuilder(axiosInstance);
  const get = <T>(url: string, config?: AxiosRequestConfig): Promise<THttpResponse<T, any>> => {
    const requestConfig: AxiosRequestConfig = {
      ...config,
    };
    if (token && !requestConfig.headers?.Authorization) {
      const headers = requestConfig.headers;
      requestConfig.headers = {
        ...headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return axiosInstance.get<T>(url, requestConfig).catch(wrapError);
  };
  const request = <T>(config: AxiosRequestConfig): Promise<THttpResponse<T, any>> => {
    const requestConfig: AxiosRequestConfig = {
      ...config,
    };
    if (token && !requestConfig.headers?.Authorization) {
      const headers = requestConfig.headers;
      requestConfig.headers = {
        ...headers,
        Authorization: `Bearer ${token}`,
      };
    }
    return axiosInstance.request<T>(requestConfig).catch(wrapError);
  };
  const service: IHttpService = {
    setBaseURL,
    setToken,
    get,
    request,
  };
  return service;
}

export default getHttpService;
